/**
 * How the CRM says things: the formatting rules the table, the side panel and
 * the window all share.
 *
 * These lived inside the page while it was one 2,000-line file. They moved out
 * with the rebuild so the three surfaces that show a contact agree on what an
 * unrecorded value looks like, what an unscored party looks like, and which
 * colour a type badge is — and so a test of one surface pins the rule for all.
 */
import type { CrmCaptureSummary, CrmCompanyType, CrmContact, CrmContactList } from '@/services/crm'
import type { RelationshipRow, RelationshipStage } from '@/services/crmRelationships'
import { parseDateValue } from '@/utils/dates'

/** What the screen shows for a value the record does not carry. */
export const NOT_ON_RECORD = 'Not on record'

/**
 * A recorded instant relative to now, or "Not on record".
 *
 * Nothing recorded is not "just now" — it is nothing recorded. And "now" is the
 * real clock: a fixed reference date was fine while every date came from the
 * sample roster, but a real last-contact date read against March 2026 rendered
 * as the future.
 */
export function relativeTime(isoDate: string | null | undefined): string {
  if (!isoDate) return NOT_ON_RECORD
  const d = new Date(isoDate)
  if (Number.isNaN(d.getTime())) return NOT_ON_RECORD
  const diffMs = Date.now() - d.getTime()
  const diffMin = Math.floor(diffMs / 60000)
  if (diffMin < 1) return 'just now'
  if (diffMin < 60) return `${diffMin}m ago`
  const diffHr = Math.floor(diffMin / 60)
  if (diffHr < 24) return `${diffHr}h ago`
  const diffDay = Math.floor(diffHr / 24)
  if (diffDay === 1) return '1 day ago'
  if (diffDay < 30) return `${diffDay}d ago`
  if (diffDay < 365) return `${Math.floor(diffDay / 30)}mo ago`
  return `${Math.floor(diffDay / 365)}y ago`
}

/** A recorded date as the desk writes it, or "Not on record". */
export function shortDate(isoDate: string | null | undefined): string {
  if (!isoDate) return NOT_ON_RECORD
  const d = parseDateValue(isoDate)
  if (!d) return NOT_ON_RECORD
  return d.toLocaleDateString(undefined, { day: 'numeric', month: 'short', year: 'numeric' })
}

/** Sort key for a possibly-unrecorded date: unrecorded sorts oldest. */
export function contactTime(isoDate: string | null | undefined): number {
  if (!isoDate) return 0
  const t = new Date(isoDate).getTime()
  return Number.isNaN(t) ? 0 : t
}

/**
 * Two letters for an avatar.
 *
 * An entity ("Harbor Point Owner LLC") has no surname, and `last[0]` on an
 * empty string is undefined — which rendered as the literal "MUNDEFINED".
 */
export function initials(first: string, last: string): string {
  const a = first.trim()[0] ?? ''
  const b = last.trim()[0] ?? first.trim()[1] ?? ''
  return `${a}${b}`.toUpperCase()
}

/** Initials for a contact, falling back to what it is called when the
 *  correspondence never stated a name. */
export function contactInitials(contact: Pick<CrmContact, 'firstName' | 'lastName' | 'displayName'>): string {
  const fromName = initials(contact.firstName ?? '', contact.lastName ?? '')
  if (fromName) return fromName
  const fallback = (contact.displayName ?? '').replace(/[^a-z0-9 ]/gi, '').trim()
  const parts = fallback.split(/\s+/).filter(Boolean)
  if (parts.length >= 2) return `${parts[0][0]}${parts[1][0]}`.toUpperCase()
  return fallback.slice(0, 2).toUpperCase()
}

/** A readable label for an observed field name the screen has no slot for. */
export function labelForField(field: string): string {
  return field.replace(/([a-z])([A-Z])/g, '$1 $2').replace(/^./, (c) => c.toUpperCase())
}

/** An unscored party is neutral, not red: red means "scored, and badly". */
export function scoreColor(score: number | null): string {
  if (score === null) return 'text-stone-500'
  if (score >= 80) return 'text-emerald-400'
  if (score >= 60) return 'text-amber-400'
  return 'text-red-400'
}

export function scoreBg(score: number | null): string {
  if (score === null) return 'bg-stone-500/10 border-stone-500/20'
  if (score >= 80) return 'bg-emerald-400/10 border-emerald-400/20'
  if (score >= 60) return 'bg-amber-400/10 border-amber-400/20'
  return 'bg-red-400/10 border-red-400/20'
}

export function typeBadgeColor(type: CrmCompanyType): string {
  switch (type) {
    case 'Broker':
      return 'bg-blue-400/10 text-blue-300 border-blue-400/20'
    case 'Borrower/Sponsor':
      return 'bg-emerald-400/10 text-emerald-300 border-emerald-400/20'
    case 'Investor':
      return 'bg-purple-400/10 text-purple-300 border-purple-400/20'
    case 'Law Firm':
      return 'bg-amber-400/10 text-amber-300 border-amber-400/20'
    case 'Appraiser':
      return 'bg-cyan-400/10 text-cyan-300 border-cyan-400/20'
    case 'Insurance':
      return 'bg-orange-400/10 text-orange-300 border-orange-400/20'
    case 'Internal':
      return 'bg-stone-400/10 text-stone-300 border-stone-400/20'
    // Captured off a deal, with nothing on record settling the side of the
    // table. Neutral on purpose: it must not read as a category.
    case 'Unknown':
    default:
      return 'bg-stone-500/10 text-stone-400 border-stone-500/20'
  }
}

/**
 * Every number on record, under the label the correspondence carried.
 *
 * A signature listing a direct line and a switchboard states two different
 * things. Where nothing labelled a number it appears once, unlabelled, rather
 * than being presented as a direct line it was never claimed to be.
 */
export function labelledPhones(
  contact: Pick<CrmContact, 'phone' | 'phones'>,
): { label: string; value: string }[] {
  const phones = contact.phones
  const rows: { label: string; value: string }[] = []
  if (phones?.direct) rows.push({ label: 'Direct', value: phones.direct })
  if (phones?.mobile) rows.push({ label: 'Mobile', value: phones.mobile })
  if (phones?.office) rows.push({ label: 'Office', value: phones.office })
  if (rows.length === 0) {
    rows.push({ label: '', value: contact.phone ?? phones?.primary ?? NOT_ON_RECORD })
  }
  return rows
}

/** Addresses beyond the primary one, without repeating it. */
export function otherEmails(contact: Pick<CrmContact, 'email' | 'emails'>): string[] {
  const primary = (contact.email ?? '').toLowerCase()
  return (contact.emails ?? []).filter((a) => a.toLowerCase() !== primary)
}

/** Every phone slot a contact carries, deduplicated, for search. */
export function allPhones(contact: Pick<CrmContact, 'phone' | 'phones'>): string[] {
  const values = [
    contact.phone,
    contact.phones?.primary,
    contact.phones?.direct,
    contact.phones?.mobile,
    contact.phones?.office,
  ].filter((v): v is string => Boolean(v))
  return Array.from(new Set(values))
}

/** Digits only, so "(201) 555-0142" is found by "2015550142". */
export function phoneDigits(phone: string): string {
  return phone.replace(/\D/g, '')
}

/**
 * Whether a contact matches a free-text search.
 *
 * Name, company, every address, every phone slot, title, capacities/tags and
 * location — the things a desk types when it is looking for someone. The old
 * search read the primary email and the company type only, so "917" found
 * nobody and a second address was invisible.
 */
export function matchesSearch(contact: CrmContact, query: string): boolean {
  const q = query.trim().toLowerCase()
  if (!q) return true
  const qDigits = q.replace(/\D/g, '')
  const haystack = [
    contact.displayName,
    `${contact.firstName} ${contact.lastName}`,
    contact.company,
    contact.title,
    contact.location,
    contact.companyType,
    ...(contact.emails ?? []),
    contact.email,
    ...(contact.tags ?? []),
    ...(contact.capacities ?? []),
  ]
    .filter((v): v is string => Boolean(v))
    .map((v) => v.toLowerCase())
  if (haystack.some((v) => v.includes(q))) return true
  if (qDigits.length >= 3) {
    return allPhones(contact).some((p) => phoneDigits(p).includes(qDigits))
  }
  return false
}

/** Plural as the desk writes it: "1 message", "4 messages". */
export function plural(count: number, noun: string, pluralNoun = `${noun}s`): string {
  return `${count} ${count === 1 ? noun : pluralNoun}`
}

/** Why a captured row carries a dot: nobody has looked at it yet. */
/** A flagged row (founder's decision, 4 Nov 2026): everything is added
 *  automatically, and only an entry the engine is unsure of is flagged. */
export const NEEDS_REVIEW_TITLE = 'Unsure entry — the engine doubts this record'

/** The hover text for a flagged row: the engine's own reason when it gave one. */
export function unsureTitle(reason?: string | null): string {
  return reason ? `Unsure: ${reason}` : NEEDS_REVIEW_TITLE
}

// ── Ordering ────────────────────────────────────────────────────────────────

export type ContactSortField =
  | 'name'
  | 'company'
  | 'type'
  | 'stage'
  | 'health'
  | 'deals'
  | 'activity'
  | 'lastContact'
  | 'score'
export type SortDir = 'asc' | 'desc'

function nameKey(contact: CrmContact): string {
  // A person sorts by surname the way a desk's roster does; an organisation
  // has no surname and sorts by what it is called.
  if (contact.kind === 'organisation' || !contact.lastName) return contact.displayName.toLowerCase()
  return `${contact.lastName} ${contact.firstName}`.toLowerCase()
}

function compareText(a: string | null | undefined, b: string | null | undefined): number {
  // Unrecorded sorts after everything recorded, in either direction: a blank
  // company is not "before A", it is nothing.
  if (!a && !b) return 0
  if (!a) return 1
  if (!b) return -1
  return a.localeCompare(b, undefined, { sensitivity: 'base' })
}

/**
 * The directory in one order.
 *
 * The default is last contact, newest first, then name — never the
 * relationship score, which the old page sorted on: nothing writes that
 * column, so it is null on every live record and the "sort" was the order
 * the rows happened to arrive in.
 */
const STAGE_RANK: Record<RelationshipStage, number> = { core: 0, active: 1, cooling: 2, new: 3 }

export function sortContacts(
  contacts: CrmContact[],
  field: ContactSortField,
  dir: SortDir,
  /** The relationship rows the stage, health and activity columns sort on. */
  relationships?: ReadonlyMap<string, RelationshipRow>,
): CrmContact[] {
  const sign = dir === 'asc' ? 1 : -1
  return [...contacts].sort((a, b) => {
    let cmp = 0
    switch (field) {
      case 'stage': {
        const ra = relationships?.get(a.id)?.stage
        const rb = relationships?.get(b.id)?.stage
        // No stage sorts last in either direction: it is nothing, not "after New".
        if (!ra && !rb) cmp = 0
        else if (!ra) return 1
        else if (!rb) return -1
        else cmp = STAGE_RANK[ra] - STAGE_RANK[rb]
        break
      }
      case 'health': {
        const ha = relationships?.get(a.id)?.health ?? null
        const hb = relationships?.get(b.id)?.health ?? null
        if (ha === null && hb === null) cmp = 0
        else if (ha === null) return 1
        else if (hb === null) return -1
        else cmp = ha - hb
        break
      }
      case 'activity': {
        const total = (id: string) => (relationships?.get(id)?.spark ?? []).reduce((sum, n) => sum + n, 0)
        cmp = total(a.id) - total(b.id)
        break
      }
      case 'name':
        cmp = nameKey(a).localeCompare(nameKey(b))
        break
      case 'company':
        cmp = compareText(a.company, b.company)
        // A blank stays last whichever way the column is sorted.
        if (!a.company !== !b.company) return cmp
        break
      case 'type':
        cmp = compareText(a.companyType, b.companyType)
        break
      case 'deals':
        cmp = a.linkedDeals.length - b.linkedDeals.length
        break
      case 'lastContact':
        cmp = contactTime(a.lastContact) - contactTime(b.lastContact)
        break
      case 'score':
        if (a.score === null && b.score === null) cmp = 0
        else if (a.score === null) return 1
        else if (b.score === null) return -1
        else cmp = a.score - b.score
        break
    }
    if (cmp !== 0) return cmp * sign
    return nameKey(a).localeCompare(nameKey(b))
  })
}

/** Lists in chip order: built-in first, in the platform's own order; then
 *  the desk's own. */
export function orderLists(lists: CrmContactList[]): CrmContactList[] {
  return [...lists].sort((a, b) => {
    if (a.isSystem !== b.isSystem) return a.isSystem ? -1 : 1
    if (a.position !== b.position) return (a.position ?? 0) - (b.position ?? 0)
    return a.name.localeCompare(b.name)
  })
}

/** The colours a chip may carry. Named, so a stored value is legible in the
 *  database and a swatch reads the same on every screen. */
export const LIST_COLOURS: { value: string; label: string; className: string }[] = [
  { value: 'amber', label: 'Gold', className: 'bg-amber-400' },
  { value: 'emerald', label: 'Green', className: 'bg-emerald-400' },
  { value: 'sky', label: 'Blue', className: 'bg-sky-400' },
  { value: 'violet', label: 'Violet', className: 'bg-violet-400' },
  { value: 'rose', label: 'Rose', className: 'bg-rose-400' },
  { value: 'stone', label: 'Grey', className: 'bg-stone-400' },
]

export function listColourClass(colour: string | null | undefined): string | null {
  return LIST_COLOURS.find((c) => c.value === colour)?.className ?? null
}

/** What the backfill read, as a sentence — with every figure it reports and
 *  none it does not. `dealsRead` and the dedup counts are newer than the
 *  route; a summary without them is not padded with zeros. */
export function captureSentence(summary: CrmCaptureSummary): string {
  const read = [plural(summary.recordsRead, 'message')]
  if (typeof summary.dealsRead === 'number') read.push(plural(summary.dealsRead, 'deal'))
  let sentence = `Read ${read.join(' and ')}; ${plural(summary.contactsOnRecord, 'contact')} on record`
  const dedup: string[] = []
  if (typeof summary.contactsMerged === 'number') dedup.push(`${summary.contactsMerged} merged`)
  if (typeof summary.namesCleaned === 'number') dedup.push(`${summary.namesCleaned} names cleaned`)
  if (dedup.length > 0) sentence += ` (${dedup.join(', ')})`
  sentence += '.'
  // What the sweep could NOT do — never folded into a clean-sounding total.
  // A zeroed dedup summary and a genuinely quiet sweep read identically
  // unless the failure is named outright.
  const failed: string[] = []
  if (summary.recordsFailed) failed.push(plural(summary.recordsFailed, 'message'))
  if (summary.dealsFailed) failed.push(plural(summary.dealsFailed, 'deal'))
  if (failed.length > 0) sentence += ` ${failed.join(' and ')} could not be read.`
  if (summary.dedupFailed) sentence += ' The dedup pass could not complete.'
  return sentence
}

/** Whether a capture summary carries a failure the note should be coloured for. */
export function captureHadFailure(summary: CrmCaptureSummary): boolean {
  return Boolean(summary.recordsFailed || summary.dealsFailed || summary.dedupFailed)
}
